import {
  CheckCircleFilled,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { useValuation } from '@api/useValuation'
import { DetailHeader } from '@components/InventoryValuation/Costing/Details/DetailHeader'
import { MiddleLayer } from '@components/InventoryValuation/Costing/Details/MiddleLayers'
import { SimpleLayers } from '@components/InventoryValuation/Costing/Details/SimpleLayers'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OrderModal } from '@components/OrderWizard/OrderModal'
import { GraviButton, Horizontal, NothingMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Tag, Tooltip } from 'antd'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'

function StatusTag(text, color, icon) {
  return (
    <Tag className='ml-4' color={color}>
      <Texto category='h3' weight='bold'>
        {icon}
        {text}
      </Texto>
    </Tag>
  )
}
function ValuationDetailStatusTag({ is_projected, has_errors }) {
  if (is_projected && has_errors) {
    return StatusTag(
      'Projected With Errors',
      'var(--theme-warning-dim)',
      <WarningFilled style={{ color: 'var(--theme-warning)' }} className='mr-2 m-1' />
    )
  }
  if (is_projected) {
    return StatusTag('Projected', 'var(--gray-200)', <ClockCircleOutlined className='mr-2 m-1' />)
  }
  if (has_errors) {
    return StatusTag(
      'Actual With Errors',
      'var(--theme-error-dim)',
      <WarningFilled style={{ color: 'var(--theme-error)' }} className='mr-2 m-1' />
    )
  }
  return StatusTag(
    'Actual',
    'var(--theme-success-dim)',
    <CheckCircleFilled style={{ color: 'var(--theme-success)' }} className='mr-2 m-1' />
  )
}
export function Details({ filterDate, selectedICI, headerData, expandedRows, setExpandedRows }) {
  const [showModal, setShowModal] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null)
  const [date, setDate] = useState(filterDate)
  const { getCostDetailsByIdQuery } = useValuation()
  const {
    refetch,
    data: detailsData,
    isPending,
    isFetching,
    isError,
  } = getCostDetailsByIdQuery(
    moment(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
    selectedICI
  )
  const { scopes } = useSecurityContext()
  const showDate = (dateFromTable, isProjected) => {
    const rowDate = moment(dateFromTable)
    if (rowDate.isSame(moment(date), 'day')) {
      return <Texto appearance={isProjected && 'secondary'}>{moment(filterDate).format('MM/DD/YYYY')}</Texto>
    }
    return (
      <Tooltip title='View this day'>
        <GraviButton
          appearance='ghost'
          style={{
            fontWeight: 'normal',
            padding: 0,
            border: 'none',
            background: 'transparent',
            textDecoration: 'underline',
          }}
          buttonText={<Texto appearance={'primary'}>{rowDate && moment(rowDate).format('MM/DD/YYYY')}</Texto>}
          onClick={() => setDate(rowDate.toISOString())}
          title='View this day'
        />
      </Tooltip>
    )
  }

  useEffect(() => {
    setDate(filterDate)
    setExpandedRows([])
  }, [filterDate, selectedICI])
  const changeDate = (add = false) => {
    if (add) {
      setDate(moment(date).add(1, 'days').startOf('day').toDate())
    } else {
      setDate(moment(date).subtract(1, 'days').startOf('day').toDate())
    }
  }
  const formattedDate = useMemo(() => {
    return moment(date).format('MMM / D')
  }, [date])

  const openOrderModal = (orderId) => {
    setSelectedOrderId(orderId)
    setShowModal(true)
  }
  const closeModal = async (isReload) => {
    setShowModal(false)
    setSelectedOrderId(null)
    if (isReload) {
      await refetch()
    }
  }

  return (
    <Vertical className='full-height-width bg-3'>
      <DetailHeader headerData={headerData} />
      <Horizontal border='bottom' className='pl-4 pr-4' alignItems='center'>
        <GraviButton appearance='outlined' theme2 icon={<LeftOutlined />} onClick={() => changeDate()} />
        <Texto category='heading' className='m-4  text-size-8'>
          {formattedDate}
        </Texto>
        <GraviButton appearance='outlined' theme2 icon={<RightOutlined />} onClick={() => changeDate(true)} />
        {detailsData && ValuationDetailStatusTag(detailsData)}
        <Tooltip title='Projected inventory costing will only use dispatched orders'>
          <InfoCircleOutlined style={{ marginLeft: 'auto', fontSize: '20px' }} />
        </Tooltip>
      </Horizontal>

      {!selectedICI || isError ? (
        <NothingMessage title='No Data' message='No rows to show' />
      ) : (
        <>
          {isPending || isFetching ? (
            <LoadingOutlined spin className='text-size-12 text-secondary m-5' />
          ) : (
            <div style={{ overflowY: 'scroll' }} className='full-height-width'>
              <Horizontal className=' p-4 mb-4' alignItems='center'>
                <Texto category='heading-small' style={{ writingMode: 'vertical-lr', transform: 'scale(-1)' }}>
                  BEGINNING
                </Texto>
                <SimpleLayers
                  data={detailsData?.deliveries_from_previous_period}
                  totalsData={detailsData?.deliveries_from_previous_period_totals}
                  openOrderModal={openOrderModal}
                  showDate={showDate}
                  scopes={scopes}
                />
              </Horizontal>

              <Horizontal className='p-4 mb-4' alignItems='center'>
                <Texto
                  category='heading-small'
                  className='text-secondary'
                  style={{ writingMode: 'vertical-lr', transform: 'scale(-1)' }}
                >
                  BUILDS / DRAWS
                </Texto>

                <MiddleLayer
                  todays_deliveries={detailsData?.todays_deliveries}
                  sale_layers={detailsData?.sale_layers}
                  inventory_costing_identifier={detailsData?.inventory_costing_identifier}
                  todays_deliveries_totals={detailsData?.todays_deliveries_totals}
                  showDate={showDate}
                  openOrderModal={openOrderModal}
                  date={date}
                  setDate={setDate}
                  setExpandedRows={setExpandedRows}
                  expandedRows={expandedRows}
                  scopes={scopes}
                  additive_draws={detailsData?.additive_draws}
                />
              </Horizontal>

              <Horizontal className=' p-4 mb-4' alignItems='center'>
                <Texto category='heading-small' style={{ writingMode: 'vertical-lr', transform: 'scale(-1)' }}>
                  FINAL
                </Texto>
                <SimpleLayers
                  data={detailsData?.unsold_deliveries}
                  totalsData={detailsData?.unsold_deliveries_totals}
                  final
                  openOrderModal={openOrderModal}
                  scopes={scopes}
                />
              </Horizontal>
              <OrderModal selectedOrder={selectedOrderId} showingModal={showModal} closeModal={closeModal} />
            </div>
          )}
        </>
      )}
    </Vertical>
  )
}
